import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { Observable } from 'rxjs';
import { PrintableCheck } from '../../models/entities/printable-check';
import { PrintableCheckBatch } from '../../models/entities/printable-check-batch';
import { HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import moment from 'moment-timezone';

@Injectable({
    providedIn: 'root',
})
export class CheckPrintingService {
    private _http: ApplicationHttpClient;

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    public getPrintingPreviewData(printedChecks: UntypedFormGroup): Observable<PrintableCheck[]> {
        this.requireAuth();
        const formData = printedChecks.getRawValue().checks.map(check => this.formatCheckData(check));

        return this._http.post(Routes.MakeLegFiCoreUrl(`/printable-checks/preview`), JSON.stringify({
                checks: formData
        }));
    }

    public submitPrintedChecks(printedChecks: any, checkFormat: any): Observable<PrintableCheckBatch> {
        const jwt = this.requireAuth();

        const formattedChecks = printedChecks.checks.map(check => this.formatCheckData(check));

        const requestData = {
            format: checkFormat.format,
            organizationId: jwt.orgId,
            checks: formattedChecks,
            displayBankAccount: checkFormat.displayBankAccount,
            displayPayor: checkFormat.displayPayor,
            displayCheckNumber: checkFormat.displayCheckNumber,
            displayMemo: checkFormat.displayMemo,
            displayVendorAddress: checkFormat.displayVendorAddress
        };

        return this._http.post(Routes.MakeLegFiCoreUrl(`/printable-checks`), JSON.stringify(requestData));
    }

    public submitExistingChecks(printedCheckIds: number[], checkFormat: any): Observable<PrintableCheckBatch> {
        const jwt = this.requireAuth();

        const requestData = {
            checkIds: printedCheckIds,
            organizationId: jwt.orgId,
            format: checkFormat.format,
            displayBankAccount: checkFormat.displayBankAccount,
            displayPayor: checkFormat.displayPayor,
            displayCheckNumber: checkFormat.displayCheckNumber,
            displayMemo: checkFormat.displayMemo,
            displayVendorAddress: checkFormat.displayVendorAddress
        };

        return this._http.post(
                Routes.MakeLegFiCoreUrl(`/printable-checks/new-batch-from-existing`),
                JSON.stringify(requestData)
        );
    }

    public downloadPrintedCheckFile(batchId: number): Observable<void> {
        const jwt = this.requireAuth();
        return this._http.get(Routes.MakeLegFiCoreUrl(`/printable-checks/${batchId}/download?organizationId=${jwt.orgId}`), {
            responseType: 'blob',
        });
    }

    public getChecks(
            search: string,
            status: string,
            sortColumn: string,
            sortDirection: string,
            pageIndex: number,
            pageSize: number,
    ): Observable<any> {
        const jwt = this.requireAuth();
        const params = [
            'search=' + encodeURIComponent(search),
            'status=' + status,
            'sort_column=' + sortColumn,
            'sort_direction=' + sortDirection,
            'per_page=' + pageSize,
            'page=' + pageIndex,
            'organization_id=' + jwt.orgId,
        ];

        const url: string = Routes.MakeLegFiCoreUrl(
                '/printable-checks?' + (params.join('&')),
        );
        return this._http.get(url);
    }

    public voidCheck(checkId: number, voidReason: string): Observable<Object> {
        this.requireAuth();
        return this._http.patch(
                Routes.MakeLegFiCoreUrl('/printable-checks/cancel/' + checkId),
                JSON.stringify({
                    voidReason: voidReason,
                }),
        );
    }

    /**
     * Look up active PrintableChecks in the user's org that match a candidate check by
     * (vendor, amount, ±windowDays of checkDate). Used by the check-printing dialog to
     * confirm intent when an apparent dup exists (SD-6308).
     */
    public findPotentialDuplicates(
            vendorId: number,
            amountCents: number,
            checkDate: string,
            windowDays?: number,
    ): Observable<PrintableCheck[]> {
        this.requireAuth();
        let params = new HttpParams()
                .set('vendorId', String(vendorId))
                .set('amount', String(amountCents))
                .set('checkDate', checkDate);
        if (windowDays !== undefined) {
            params = params.set('windowDays', String(windowDays));
        }
        return this._http.get(Routes.MakeLegFiCoreUrl('/printable-checks/potential-duplicates'), {params}).pipe(
                map((response: PrintableCheck[]) => response.map(item => new PrintableCheck(item))),
        );
    }

    public getChecksByIds(checkIds: number[]): Observable<PrintableCheck[]> {
        const jwt = this.requireAuth();
        let params = new HttpParams();
        checkIds.forEach(id => {
            params = params.append('checkIds[]', id);
        });
        params = params.append('organization_id', jwt.orgId);
        return this._http.get(Routes.MakeLegFiCoreUrl(`/printable-checks`), {
            params: params,
        }).pipe(
                map((response: PrintableCheck[]) => {
                    return response.map(item => new PrintableCheck(item));
                }),
        );
    }

    private formatCheckData(check: any): any {
        const amount = typeof check.amount === 'string' ?
                parseInt(check.amount.replace(/[^0-9]/g, ''), 10) :
                check.amount;

        let checkNumber = null;
        if (check.checkNumber) {
            checkNumber = typeof check.checkNumber === 'string' ?
                    parseInt(check.checkNumber, 10) :
                    check.checkNumber;
        }

        let checkDate = check.checkDate;
        if (checkDate) {
            if (moment.isMoment(checkDate)) {
                checkDate = checkDate.format('YYYY-MM-DD');
            } else if (checkDate instanceof Date) {
                checkDate = moment(checkDate).format('YYYY-MM-DD');
            }
        }

        return {
            vendorId: check.vendorId || null,
            payee: check.payee || '',
            memo: check.memo || null,
            amount: amount,
            checkDate: checkDate,
            checkNumber: checkNumber,
            lobBankAccountId: check.lobBankAccountId || null
        };
    }

    private requireAuth(): JwtLegFiClaims {
        const jwt = LegFiJwtService.read();
        if (jwt === null) {
            throw this._http.redirectAndThrow401Observable();
        }
        return jwt;
    }
}
